// Trang tạo ma trận đề kiểm tra

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, FileText, Download, Loader2 } from 'lucide-react'
import { ContentSidebar } from '../../components/exam-matrix/ContentSidebar'
import { ExamMatrixTable } from '../../components/exam-matrix/ExamMatrixTable'
import { ExamMatrix, ExamMatrixRow, DangToan, QuestionCell, ContentNode } from '../../types/exam-matrix'
import { examMatrixService } from '../../services/exam-matrix/ExamMatrixService'
import { bankQuestionService } from '../../services/bankQuestionService_moi'
import { getTenBai, getChuongName } from '../../config/dang-toan-mapping'
import toast from 'react-hot-toast'

export const TaoMatranDeKiemTra: React.FC = () => {
  const navigate = useNavigate()

  // State cấu hình
  const [lop, setLop] = useState<number>(11)
  const [loaiKiemTra, setLoaiKiemTra] = useState<'KTGKI' | 'KTCKI' | 'KTGKII' | 'KTCKII'>('KTGKI')
  const [selectedChuong, setSelectedChuong] = useState<number[]>([1])
  const [tenMaTran, setTenMaTran] = useState<string>('')
  const [thoiGianLamBai, setThoiGianLamBai] = useState<number>(45)
  const [diemDat, setDiemDat] = useState<number>(50)

  // State ma trận
  const [matrixRows, setMatrixRows] = useState<ExamMatrixRow[]>([])
  const [addedDangs, setAddedDangs] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(false)

  // Tạo empty cell
  const createEmptyCell = (defaultVal: number = 0): QuestionCell => ({
    soCauYeuCau: defaultVal,
    soCauCoSan: 0,
    isValid: true
  })

  // Tạo empty dạng toán
  const createEmptyDang = (tenDang: string): DangToan => ({
    tenDang,
    tnkq: {
      nhieuLuaChon: {
        biet: createEmptyCell(0),
        hieu: createEmptyCell(0),
        vanDung: createEmptyCell(0)
      },
      dungSai: { // Mặc định 2 câu (1 biết, 1 hiểu)
        biet: createEmptyCell(1),
        hieu: createEmptyCell(1),
        vanDung: createEmptyCell(0)
      },
      traLoiNgan: { // Mặc định 4 câu (2 biết, 2 hiểu)
        biet: createEmptyCell(2),
        hieu: createEmptyCell(2),
        vanDung: createEmptyCell(0)
      }
    },
    tuLuan: { // Mặc định 3 câu (1 biết, 1 hiểu, 1 vận dụng)
      biet: createEmptyCell(1),
      hieu: createEmptyCell(1),
      vanDung: createEmptyCell(1)
    }
  })

  // Update số câu có sẵn cho dạng
  const updateDangWithAvailableQuestions = (dang: DangToan, available: Record<string, number>) => {
    dang.tnkq.nhieuLuaChon.biet.soCauCoSan = available['TN_biet'] || 0
    dang.tnkq.nhieuLuaChon.hieu.soCauCoSan = available['TN_hieu'] || 0
    dang.tnkq.nhieuLuaChon.vanDung.soCauCoSan = available['TN_vanDung'] || 0

    dang.tnkq.dungSai.biet.soCauCoSan = available['DS_biet'] || 0
    dang.tnkq.dungSai.hieu.soCauCoSan = available['DS_hieu'] || 0
    dang.tnkq.dungSai.vanDung.soCauCoSan = available['DS_vanDung'] || 0

    dang.tnkq.traLoiNgan.biet.soCauCoSan = available['TL_biet'] || 0
    dang.tnkq.traLoiNgan.hieu.soCauCoSan = available['TL_hieu'] || 0
    dang.tnkq.traLoiNgan.vanDung.soCauCoSan = available['TL_vanDung'] || 0

    dang.tuLuan.biet.soCauCoSan = available['TLU_biet'] || 0
    dang.tuLuan.hieu.soCauCoSan = available['TLU_hieu'] || 0
    dang.tuLuan.vanDung.soCauCoSan = available['TLU_vanDung'] || 0
  }

  // Xử lý thêm dạng toán
  const handleAddDang = async (node: ContentNode) => {
    const { lop, chuong, bai, dang } = node

    if (!bai || !dang) return

    const dangKey = `L${lop}_C${chuong}_B${bai}_${dang}`

    if (addedDangs.has(dangKey)) {
      toast('Dạng toán này đã được thêm')
      return
    }

    setIsLoading(true)

    try {
      const existingRowIndex = matrixRows.findIndex(
        row => row.lop === lop && row.chuong === chuong && row.bai === bai
      )

      const available = await bankQuestionService.getAvailableQuestionsForRow(lop, chuong, bai)
      const newDang = createEmptyDang(dang)
      updateDangWithAvailableQuestions(newDang, available)

      if (existingRowIndex >= 0) {
        const updatedRows = [...matrixRows]
        updatedRows[existingRowIndex].cacDang.push(newDang)
        setMatrixRows(updatedRows)
      } else {
        const newRow: ExamMatrixRow = {
          id: `row_${Date.now()}_${Math.random()}`,
          stt: matrixRows.length + 1,
          lop,
          chuong,
          bai,
          chuDe: getChuongName(lop, chuong),
          baiHoc: getTenBai(lop, chuong, bai),
          cacDang: [newDang]
        }

        setMatrixRows([...matrixRows, newRow])
      }

      setAddedDangs(new Set([...addedDangs, dangKey]))
      toast.success(`Đã thêm: ${dang}`)

    } catch (error) {
      console.error('Error adding dang:', error)
      toast.error('Không thể thêm dạng toán')
    } finally {
      setIsLoading(false)
    }
  }

  // Xử lý xóa dạng
  const handleDeleteDang = (rowId: string, dangIndex: number) => {
    const updatedRows = matrixRows.map(row => {
      if (row.id === rowId) {
        const dang = row.cacDang[dangIndex]
        const dangKey = `L${row.lop}_C${row.chuong}_B${row.bai}_${dang.tenDang}`

        const newAddedDangs = new Set(addedDangs)
        newAddedDangs.delete(dangKey)
        setAddedDangs(newAddedDangs)

        const newDangs = row.cacDang.filter((_, index) => index !== dangIndex)
        return { ...row, cacDang: newDangs }
      }
      return row
    })

    const filteredRows = updatedRows.filter(row => row.cacDang.length > 0)
    setMatrixRows(filteredRows)
    toast.success('Đã xóa dạng toán')
  }

  // Xử lý xóa row
  const handleDeleteRow = (rowId: string) => {
    const row = matrixRows.find(r => r.id === rowId)
    if (!row) return

    const newAddedDangs = new Set(addedDangs)
    row.cacDang.forEach(dang => {
      const dangKey = `L${row.lop}_C${row.chuong}_B${row.bai}_${dang.tenDang}`
      newAddedDangs.delete(dangKey)
    })
    setAddedDangs(newAddedDangs)

    setMatrixRows(matrixRows.filter(r => r.id !== rowId))
    toast.success('Đã xóa bài học')
  }

  // Xử lý update row
  const handleUpdateRow = (rowId: string, updates: Partial<ExamMatrixRow>) => {
    setMatrixRows(matrixRows.map(row =>
      row.id === rowId ? { ...row, ...updates } : row
    ))
  }

  // Xử lý lưu ma trận
  const handleSaveMatrix = async () => {
    if (!tenMaTran.trim()) {
      toast.error('Vui lòng nhập tên ma trận')
      return
    }

    if (matrixRows.length === 0) {
      toast.error('Ma trận chưa có nội dung')
      return
    }

    const matrix: ExamMatrix = {
      id: `matrix_${Date.now()}`,
      tenMaTran,
      loaiKiemTra,
      lop,
      cacChuong: selectedChuong,
      rows: matrixRows,
      thoiGianLamBai,
      diemDat,
      createdAt: new Date(),
      updatedAt: new Date()
    }

    try {
      await examMatrixService.saveMatrix(matrix)
      toast.success('Đã lưu ma trận thành công')
    } catch (error) {
      toast.error('Không thể lưu ma trận')
    }
  }

  // Xử lý tạo đề thi
  const handleGenerateExam = async () => {
    if (matrixRows.length === 0) {
      toast.error('Ma trận chưa có nội dung')
      return
    }

    const matrix: ExamMatrix = {
      id: `matrix_temp_${Date.now()}`,
      tenMaTran: tenMaTran || 'Đề thi tạm',
      loaiKiemTra,
      lop,
      cacChuong: selectedChuong,
      rows: matrixRows,
      thoiGianLamBai,
      diemDat,
      createdAt: new Date(),
      updatedAt: new Date()
    }

    const validation = await examMatrixService.validateMatrix(matrix)

    if (!validation.valid) {
      toast.error('Ma trận chưa hợp lệ')
      validation.errors.forEach(err => {
        toast.error(err, { duration: 5000 })
      })
      return
    }

    setIsLoading(true)
    try {
      const quizData = await examMatrixService.generateExamFromMatrix(matrix)

      // Navigate to quiz interface - SỬ DỤNG GIAO DIỆN ĐÃ CÓ
      navigate(`/trac-nghiem/custom/${quizData.id}`, {
        state: { quizData }
      })
    } catch (error) {
      toast.error('Không thể tạo đề thi')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar bên trái */}
      <div className="w-80 flex-shrink-0 shadow-lg">
        <ContentSidebar
          lop={lop}
          selectedChuong={selectedChuong}
          onAddDang={handleAddDang}
          addedDangs={addedDangs}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b shadow-sm px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/trac-nghiem')}
                className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Quay lại
              </button>
              <h1 className="text-2xl font-bold text-gray-800">Tạo Ma Trận Đề Kiểm Tra</h1>
            </div>
          </div>

          {/* Config row */}
          <div className="grid grid-cols-6 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lớp</label>
              <select
                value={lop}
                onChange={(e) => setLop(parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>Lớp 10</option>
                <option value={11}>Lớp 11</option>
                <option value={12}>Lớp 12</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Loại KT</label>
              <select
                value={loaiKiemTra}
                onChange={(e) => setLoaiKiemTra(e.target.value as any)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="KTGKI">KTGKI</option>
                <option value="KTCKI">KTCKI</option>
                <option value="KTGKII">KTGKII</option>
                <option value="KTCKII">KTCKII</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tên ma trận</label>
              <input
                type="text"
                value={tenMaTran}
                onChange={(e) => setTenMaTran(e.target.value)}
                placeholder="VD: KTGKI - Chương 1,2"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Thời gian (phút)</label>
              <input
                type="number"
                value={thoiGianLamBai}
                onChange={(e) => setThoiGianLamBai(parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Điểm đạt (%)</label>
              <input
                type="number"
                value={diemDat}
                onChange={(e) => setDiemDat(parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Chọn chương */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Chọn chương</label>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4].map(c => (
                <label key={c} className="inline-flex items-center px-3 py-1.5 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedChuong.includes(c)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedChuong([...selectedChuong, c])
                      } else {
                        setSelectedChuong(selectedChuong.filter(ch => ch !== c))
                      }
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm">Chương {c}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center space-x-3">
            <button
              onClick={handleSaveMatrix}
              disabled={isLoading}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Save className="w-4 h-4 mr-2" />
              Lưu ma trận
            </button>

            <button
              onClick={handleGenerateExam}
              disabled={isLoading || matrixRows.length === 0}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Đang tạo...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Tạo đề thi
                </>
              )}
            </button>
          </div>
        </div>

        {/* Bảng ma trận */}
        <div className="flex-1 overflow-auto p-6">
          {matrixRows.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Chưa có nội dung</p>
                <p className="text-sm mt-2">Chọn dạng toán từ sidebar bên trái để bắt đầu</p>
              </div>
            </div>
          ) : (
            <ExamMatrixTable
              rows={matrixRows}
              onUpdateRow={handleUpdateRow}
              onDeleteRow={handleDeleteRow}
              onDeleteDang={handleDeleteDang}
            />
          )}
        </div>
      </div>
    </div>
  )
}

